import { Controller } from '@hotwired/stimulus';
import { isDesktop } from '../utils/dev';
/*
 * Description
 * -------
 *
 * Handles comment form.
 * Can be used on any element.
 * Can be used multiple times in the document.
 *
 * Targets
 * -------
 *
 * - editor - (required) - comment editor element
 * - body   - (required) - hidden comment body input element
 * - submit - (required) - submit button
 *
 * Values
 * -------
 *
 * - max-length     - (required) - maximum length of the comment
 * - placeholder    - (required) - placeholder text for the comment editor
 * - editorContent  - (optional) - initial content for the comment editor
 *
 * Usage
 * -------
 *
 * <div data-controller="comment-form">
 *   <form data-action="turbo:submit-end->comment-form#reset">
 *     <input data-comment-form-target="editor" />
 *     <input data-comment-form-target="body" type="hidden" />
 *     <button data-comment-form-target="submit">Submit</button>
 *   </form>
 * </div>
 */
const IMAGE_REPLACE_CHARACTER = 'ค';
export default class default_1 extends Controller {
    static targets = ['editor', 'submit', 'body', 'toolbar', 'spoilerButtonOn', 'spoilerButtonOff'];
    static values = {
        maxLength: Number,
        placeholder: String,
        editorContent: String,
    };
    async connect() {
        const { default: Quill } = await import('quill');
        // Custom blot for spoilers
        let Inline = Quill.import('blots/inline');
        // @ts-ignore
        class SpoilerBlot extends Inline {
        }
        // @ts-ignore
        SpoilerBlot.blotName = 'spoiler';
        // @ts-ignore
        SpoilerBlot.tagName = 'spoiler';
        // @ts-ignore
        Quill.register(SpoilerBlot);
        this.quill = new Quill(this.editorTarget, {
            theme: 'snow',
            modules: { toolbar: false },
            formats: ['image', 'spoiler'],
            placeholder: this.placeholderValue,
        });
        // Set enterkeyhint="enter" on editor's first child (contenteditable element)
        this.editorTarget.firstElementChild?.setAttribute('enterkeyhint', 'enter');
        // Set initial content if it exists
        if (this.editorContentValue) {
            const delta = this.quill.clipboard.convert({ html: this.editorContentValue });
            this.quill.setContents(delta, 'silent');
        }
        // Show or hide toolbar based on the selection
        this.quill.on('editor-change', () => {
            if (!window.__fw_app__.features.spoilersTooltip)
                return;
            if (!this.hasToolbarTarget)
                return;
            if (!isDesktop())
                return;
            const selection = this.quill.getSelection();
            if (selection && selection?.length > 0) {
                const text = this.quill.getText(selection.index, selection.length);
                const hasSpoilerText = this.quill.getFormat(selection?.index, selection?.length).spoiler;
                const textExtendedByTwo = this.quill.getText(selection.index - 2, selection.length + 4);
                const hasSpoilerTextExtendedByTwo = this.quill.getFormat(selection?.index - 2, selection?.length + 4).spoiler;
                const bounds = this.quill.getBounds(selection);
                if (text.includes('\n'))
                    return this.toolbarTarget.classList.add('hidden');
                if (textExtendedByTwo.startsWith('||') &&
                    textExtendedByTwo.endsWith('||') &&
                    hasSpoilerTextExtendedByTwo) {
                    this.spoilerButtonOnTarget.classList.add('hidden');
                    this.spoilerButtonOffTarget.classList.remove('hidden');
                }
                else if (text.startsWith('||') && text.endsWith('||') && hasSpoilerText) {
                    this.spoilerButtonOnTarget.classList.add('hidden');
                    this.spoilerButtonOffTarget.classList.remove('hidden');
                }
                else {
                    this.spoilerButtonOnTarget.classList.remove('hidden');
                    this.spoilerButtonOffTarget.classList.add('hidden');
                }
                this.toolbarTarget.classList.remove('hidden');
                if (bounds) {
                    const tooltipWidth = this.toolbarTarget.offsetWidth;
                    this.toolbarTarget.style.top = `${bounds.top - 40}px`;
                    if (tooltipWidth > bounds.width) {
                        this.toolbarTarget.style.left = `${bounds.left - (tooltipWidth - bounds.width) / 2}px`;
                    }
                    else if (tooltipWidth < bounds.width) {
                        this.toolbarTarget.style.left = `${bounds.left + (bounds.width - tooltipWidth) / 2}px`;
                    }
                    else {
                        this.toolbarTarget.style.left = `${bounds.left}px`;
                    }
                }
            }
            else {
                this.toolbarTarget.classList.add('hidden');
            }
        });
        this.quill.on('text-change', () => {
            const length = this.quill.getText().trim().length;
            // Show or hide submit button based on the length of the comment
            // If the comment contains an image/custom emoji, it should be considered proper length
            if (length > 1 || this.quill.getSemanticHTML().includes('img')) {
                this.showSubmit();
            }
            else {
                this.hideSubmit();
            }
            // Delete text if it exceeds the maximum length
            if (this.quill.getLength() > this.maxLengthValue) {
                this.quill.deleteText(this.maxLengthValue, this.quill.getLength());
            }
            if (window.__fw_app__.features.spoiler) {
                this.addSpoilers();
                this.removeIncorrectSpoilers();
                this.addSpoilers();
                this.removeIncorrectSpoilers();
            }
            // Update body input with the current content of the editor
            this.bodyTarget.value = this.quill.getSemanticHTML();
        });
        // Override Enter key binding
        this.quill.keyboard.bindings['Enter'].unshift({
            key: 13,
            handler: () => this.submitTarget.click(),
        });
        // Override Cmd+E to toggle emoji picker
        this.quill.keyboard.addBinding({
            key: 'e',
            shortKey: true,
            handler: () => {
                this.dispatch('toggleEmojiPicker');
                return false;
            },
        });
    }
    showSubmit() {
        this.submitTargets.forEach((submit) => {
            submit.disabled = false;
            submit.classList.remove('comment-form__post-button--hidden');
            submit.classList.add('comment-form__post-button--visible');
        });
    }
    hideSubmit() {
        this.submitTargets.forEach((submit) => {
            submit.disabled = true;
            submit.classList.remove('comment-form__post-button--visible');
            submit.classList.add('comment-form__post-button--hidden');
        });
    }
    handleEmojiSelect(event) {
        const selection = this.quill.getSelection();
        const index = selection?.index || 0;
        if (event.detail.native) {
            this.quill.insertText(index, event.detail.native);
        }
        else {
            this.quill.insertEmbed(index, 'image', event.detail.src);
            // Add alt attribute to emoji
            const delta = this.quill.getContents();
            const addedEmoji = delta.ops.filter((op) => {
                if (!(typeof op.insert === 'object'))
                    return false;
                if (!op.insert.image)
                    return false;
                return op.insert.image === event.detail.src;
            });
            if (addedEmoji) {
                addedEmoji.forEach((emoji) => {
                    emoji.attributes = { alt: event.detail.id };
                });
            }
            this.quill.setContents(delta);
            // Set caret position to right after emoji
            this.quill.setSelection(index + 1, 0);
        }
    }
    addSpoilers() {
        const text = this.getText();
        // const spoilerRegex = new RegExp(/\|\|[^|]*[^\s|]\s*\|\|/g)
        // const spoilerRegex = new RegExp(/\|\|[^|\n\r]*[^\s|\n\r][\w|\d| ][^|\n\r]*\|\|/g)
        // const spoilerRegex = new RegExp(/\|\|[^|\n\r]*\|\|/g)
        // const spoilerRegex = new RegExp(/\|\|[^|\n\r]*?\|\|/g)
        const spoilerRegex = new RegExp(/\|\|[^|\n\r]*[^\s|\n\r][\w|\d| ]?[^|\n\r]*?\|\|/g);
        const spoilers = text.match(spoilerRegex);
        if (!spoilers?.length)
            return;
        spoilers.forEach((spoiler) => {
            // Get all occurrences of the spoiler in the text
            const positions = this.indexesOf(text, spoiler);
            positions.forEach((position) => {
                const textToFormat = text.substring(position, position + spoiler.length);
                const indexOfImage = textToFormat.indexOf(IMAGE_REPLACE_CHARACTER);
                const fromImageToEnd = textToFormat.substring(indexOfImage);
                if (indexOfImage > -1) {
                    // Wrap text before image in spoiler
                    this.quill.formatText(position, indexOfImage, 'spoiler', true, 'silent');
                    // Wrap image in spoiler
                    this.quill.formatText(position + indexOfImage, 1, 'spoiler', true, 'silent');
                    // Wrap text after image in spoiler
                    this.quill.formatText(position + indexOfImage + 1, fromImageToEnd.length, 'spoiler', true, 'silent');
                }
                else {
                    this.quill.formatText(position, spoiler.length, 'spoiler', true, 'silent');
                }
                // Remove spoiler formatting from the end of the spoiler
                this.quill.formatText(position + spoiler.length, 1, 'spoiler', false, 'silent');
            });
        });
    }
    removeIncorrectSpoilers() {
        const text = this.getText();
        const spoilerTagsRegex = new RegExp(/(\<spoiler\>(.+?)\<\/spoiler\>)/g);
        const spoilerTags = this.quill.getSemanticHTML().match(spoilerTagsRegex);
        if (!spoilerTags?.length)
            return;
        spoilerTags.forEach((spoilerTag) => {
            const spoilerText = spoilerTag.match(/<spoiler>(.+?)<\/spoiler>/)?.[1];
            // Replace all images with a single character and replace all &nbsp; with spaces
            const formattedSpoilerText = spoilerText
                ?.replaceAll(/<img([^\n]+?)\>/g, IMAGE_REPLACE_CHARACTER)
                .replaceAll('&nbsp;', ' ');
            if (!formattedSpoilerText)
                return;
            if (
            // Remove spoiler formatting if it has only '|' characters
            formattedSpoilerText.replaceAll('|', '').trim().length === 0 ||
                // Remove spoiler formatting if it is not wrapped in '||'
                !formattedSpoilerText.startsWith('||') ||
                !formattedSpoilerText.endsWith('||') ||
                formattedSpoilerText.length <= 4) {
                const positions = this.indexesOf(text, formattedSpoilerText);
                positions.forEach((position) => {
                    this.quill.formatText(position, formattedSpoilerText.length, 'spoiler', false, 'silent');
                });
            }
        });
    }
    addSpoiler() {
        const selection = this.quill.getSelection();
        if (!selection)
            return;
        this.quill.insertText(selection.index, '||');
        this.quill.insertText(selection.index + selection.length + 2, '||');
        this.quill.setSelection(selection.index, selection.length + 4);
    }
    removeSpoiler() {
        const selection = this.quill.getSelection();
        if (!selection)
            return;
        const text = this.quill.getText(selection.index, selection.length);
        const textExtendedByTwo = this.quill.getText(selection.index - 2, selection.length + 4);
        if (textExtendedByTwo.startsWith('||') && textExtendedByTwo.endsWith('||')) {
            this.quill.deleteText(selection.index - 2, 2);
            this.quill.deleteText(selection.index + selection.length - 4 + 2, 2);
        }
        else if (text.startsWith('||') && text.endsWith('||')) {
            this.quill.deleteText(selection.index, 2);
            this.quill.deleteText(selection.index + selection.length - 4, 2);
        }
    }
    getText() {
        let text = '';
        // Replace Deltas to text.
        // quill.getText() method returns text without images so it is not possible to format spoilers properly.
        this.quill.getContents().forEach((op) => {
            if (op.insert && typeof op.insert === 'string') {
                text += op.insert;
            }
            // Treat image as a single character
            if (op.insert && typeof op.insert === 'object' && op.insert.image) {
                text += IMAGE_REPLACE_CHARACTER;
            }
        });
        return text;
    }
    indexesOf(string, regex) {
        const length = (x) => x.length;
        const sum = (a, b) => a + b;
        return string
            .split(regex)
            .slice(0, -1)
            .map(length)
            .map((_, i, lengths) => lengths.slice(0, i + 1).reduce(sum, i * regex.length));
    }
    reset(event) {
        if (event.detail.success) {
            this.bodyTarget.value = '';
            this.quill.setText('');
            this.hideSubmit();
        }
    }
}
